import React, { useMemo, useState } from 'react';
import type { MultipleChoicePredictiveQuestionDto } from "@advanced-sales-technologies/backend-client/models/MultipleChoicePredictiveQuestionDto";
import type { MultipleChoicePredictiveQuestionOptionDto } from "@advanced-sales-technologies/backend-client/models/MultipleChoicePredictiveQuestionOptionDto";

import { ComponentField } from '@/types/DynamicForm.type';
import { SelectOption } from '@/types/SelectTypes.type';
import { getQuestionLabel } from '@/utils/dynamicForm.util';
import FormComboBox from '@/components/molecules/form/FormComboBox';

type Props = Extract<ComponentField, { fieldType: 'comboBoxMultiple' }>;

/**
 * Dynamic form field component for multiple choice combo box selection
 * Handles MultipleChoicePredictiveQuestionDto processing with options transformation
 * 
 * @param cfg - Field configuration object
 * @returns JSX element for the combo box field
 */
const DynamicFormComboBoxMultiple: React.FC<Props> = (cfg) => {
  const [query, setQuery] = useState("");

  const question = cfg.question?.data as MultipleChoicePredictiveQuestionDto;

  const toOptions = (choices: MultipleChoicePredictiveQuestionOptionDto[]): SelectOption[] =>
    (choices ?? []).map(({ value, is_selectable, children, ...rest }) => ({
      ...rest,
      name: value,
      isSelectable: is_selectable,
      children: toOptions(children),
    }));

  const options = useMemo(() => toOptions(question?.choices), [question?.choices]);

  return (
    <FormComboBox
      {...cfg}
      query={query}
      setQuery={setQuery}
      label={getQuestionLabel(cfg)}
      optionsSearchable={true}
      multiple={true}
      allowFreeText={false}
      options={options}
      showOptionsOnFocus
      chipClassName={"max-w-1/2"}
      helperTextBelowLabel
    />
  );
};

export default DynamicFormComboBoxMultiple;